import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { IFormChangeAddress } from '@acorn-portal/shared/src/pages/mta/change-of-address';
import { IChangeUsageOrMileageForm } from '@acorn-portal/shared/src/pages/mta/change-usage-or-mileage';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
	MtaChangeType,
	TransactionType,
	Address,
	CvMtaModel,
	Modification,
	PaymentProvider,
	MidTermAdjustmentClient,
	PaymentClient,
	PreparePaymentSource,
	TakeUpMidTermAdjustmentCommand,
} from '@acorn-portal/shared/src/services/api';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	IMidTermAdjustmentState,
	setVehicleIsEligible,
	updateMidTermAdjustmentDetails,
	INITIAL_STATE,
} from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { updateApiMTA } from '@acorn-portal/shared/src/store/midTermAdjustmentAPI.sclice';
import { isUsedForBusiness } from '@acorn-portal/shared/src/utils';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import { VehicleLookupResponse } from '@acorn-portal/shared/src/services/lookupApi';
import {
	CvMidTermAdjustmentClient as CvMidTermAdjustmentClientV2,
	MidTermAdjustmentClient as MidTermAdjustmentClientV2,
	PcMidTermAdjustmentClient as PcMidTermAdjustmentClientV2,
	TakeUpBraintreeMidTermAdjustmentCommand,
} from '@acorn-portal/shared/src/services/mtaApiV2';
import mtaHandleRating from '../utils/mta-handle-rating';

interface GetOrCreateMidTermAdjustment {
	changeType: MtaChangeType;
	setAdjustmentLoading: (isLoading: boolean) => void;
}

export interface IChangeOfVehiclePart2 {
	registeredKeeper: string;
	owner: string;
	hasModifications: string;
	imported: string;
	value: string;
	purchaseDate: {
		year: string;
		month: string;
	};
	levelOfCover: string;
	countryOfRegistration: string;
	modifications: Modification[];
	isLeftHandDrive: string;
	businessRadius: string;
	hasInternalRacking: string;
	vehicleCarriesDangerousGoods: string;
	vehicleVisitsHazardousSites: string;
	telematicsBox: string;
	vehicleUse?: string;
	licencingAuthority?: string;
	operatingArea?: string;
	bodyType?: string;
	keptOvernight: string;
	overnightAddress: Address;
	overnightAddressIsAway: string;
}

const useGetOrCreateMidTermAdjustment = ({ changeType, setAdjustmentLoading }: GetOrCreateMidTermAdjustment) => {
	const dispatch = useAppDispatch();
	const midTermAdjustment = useAppSelector((redux) => redux.midTermAdjustment);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const isLoadingRef = useRef(false);

	const { mtaStartDate, transactionId } = midTermAdjustment;

	const navigate = useNavigate();
	useEffect(() => {
		if (midTermAdjustment.policyReference === '') {
			navigate(ROUTE_PATHS.home);
			return;
		}
		const getMidTermAdjustmentDetails = async () => {
			if (isLoadingRef.current) {
				return;
			}

			isLoadingRef.current = true;
			setAdjustmentLoading(true);
			let result: CvMtaModel;
			const command = {
				policyToken: fullPolicy.policyToken,
				mtaStartDate: mtaStartDate,
				changeType: [...midTermAdjustment.changeType, changeType],
				transactionId: transactionId === '' ? undefined : transactionId,
			};

			if (fullPolicy.product === Product.COMMERCIAL_VEHICLE || fullPolicy.product === Product.TAXI) {
				const client = new CvMidTermAdjustmentClientV2(undefined, API);
				const mtaResult = await client.getOrCreateCvMidTermAdjustment(command);
				result = {
					...mtaResult,
					vehicleDetails: {
						...INITIAL_STATE.vehicleDetails,
						...mtaResult.vehicleDetails,
					},
					policyDetails: {
						...INITIAL_STATE.policyDetails,
						...mtaResult.policyDetails,
						operatingArea: fullPolicy.coverDetails.operatingArea,
					},
				};
			} else {
				const client = new PcMidTermAdjustmentClientV2(undefined, API);
				const pcResult = await client.getOrCreatePcMidTermAdjustment(command);
				result = {
					...pcResult,
					vehicleDetails: {
						...INITIAL_STATE.vehicleDetails,
						...pcResult.vehicleDetails,
					},
				};
			}

			dispatch(updateMidTermAdjustmentDetails({ ...result, policyToken: fullPolicy.policyToken }));
			dispatch(updateApiMTA(result));
			setAdjustmentLoading(false);
			isLoadingRef.current = false;
		};
		if (!midTermAdjustment.changeType.includes(changeType) && !midTermAdjustment.hasInitialData) {
			getMidTermAdjustmentDetails().catch(() => {
				setAdjustmentLoading(false);
				isLoadingRef.current = false;
				navigate(ROUTE_PATHS.error);
			});
		}
	}, [dispatch]);
	return { midTermAdjustment, setAdjustmentLoading };
};

export const useCheckVehicleIsMtaEligible = () => {
	const dispatch = useAppDispatch();
	const midTermAdjustment = useAppSelector((redux) => redux.midTermAdjustment);
	const policy = useAppSelector((redux) => redux.fullPolicy);
	const isLoadingRef = useRef(false);

	useEffect(() => {
		const checkVehicleIsMtaEligible = async () => {
			if (isLoadingRef.current) {
				return;
			}
			isLoadingRef.current = true;
			if (!policy?.scheme) return;
			const client = new MidTermAdjustmentClient(undefined, API);
			const result = await client.allowVehicleMTA(
				policy.policyToken,
				policy.startDate,
				policy.endDate,
				policy.product,
				policy.scheme,
			);
			dispatch(setVehicleIsEligible(result.allowed ?? false));
		};
		if (policy !== undefined && policy.policyNumber !== '') {
			checkVehicleIsMtaEligible().catch(console.error);
		}
	}, [dispatch]);

	return { isEligible: midTermAdjustment.isVehicleEligible };
};

export const handleChangeOfAddressRating = async ({
	midTermAdjustment,
	values,
	product,
}: {
	midTermAdjustment: IMidTermAdjustmentState;
	values: IFormChangeAddress;
	product: Product;
}) => {
	const command = {
		changeType: [...midTermAdjustment.changeType, MtaChangeType.CHANGE_ADDRESS],
		mtaStartDate: midTermAdjustment.mtaStartDate,
		policyToken: midTermAdjustment.policyToken,
		midTermAdjustment: {
			...midTermAdjustment,
			policyDetails: {
				...midTermAdjustment.policyDetails,
				policyholder: {
					...midTermAdjustment.policyDetails.policyholder,
					address: values.address,
					isHomeOwner: values.isHomeOwner === 'yes',
				},
				operatingArea: values.operatingArea,
			},
			vehicleDetails: {
				...midTermAdjustment.vehicleDetails,
				annualMileage: values.annualMileage ? parseInt(values.annualMileage, 10) : 0,
				businessMileage: isUsedForBusiness(midTermAdjustment.vehicleDetails.vehicleUse)
					? parseInt(values.businessMileage, 10)
					: 0,

				vehicleOvernight: values.keptOvernight,

				licencingAuthority: values.licencingAuthority,
				overnightAddress: values.overnightAddress ? values.overnightAddress : values.address,
				operatingArea: values.operatingArea,
			},
		},
	};

	return mtaHandleRating(product, command);
};

export const handleChangeOfUsageOrMilageRating = async ({
	midTermAdjustment,
	values,
	product,
}: {
	midTermAdjustment: IMidTermAdjustmentState;
	values: IChangeUsageOrMileageForm;
	product: Product;
}) => {
	const command = {
		changeType: [...midTermAdjustment.changeType, MtaChangeType.CHANGE_USAGE_AND_MILEAGE],
		mtaStartDate: midTermAdjustment.mtaStartDate,
		policyToken: midTermAdjustment.policyToken,
		midTermAdjustment: {
			...midTermAdjustment,
			policyDetails: {
				...midTermAdjustment.policyDetails,
				operatingArea: values.operatingArea,
			},
			vehicleDetails: {
				...midTermAdjustment.vehicleDetails,
				annualMileage: values.annualMileage ? parseInt(values.annualMileage, 10) : 0,
				businessMileage: values.businessMileage ? parseInt(values.businessMileage, 10) : 0,
				coverDefinitionCode: values.levelOfCover,
				vehicleUse: values.vehicleUse,
				licencingAuthority: values.licencingAuthority,
			},
		},
	};

	return mtaHandleRating(product, command);
};

export const handleTopUpMilesRating = async ({
	midTermAdjustment,
	annualMileage,
	product,
}: {
	midTermAdjustment: IMidTermAdjustmentState;
	annualMileage: number;
	product: Product;
}) => {
	const command = {
		changeType: [...midTermAdjustment.changeType, MtaChangeType.TOP_UP_MILES],
		mtaStartDate: midTermAdjustment.mtaStartDate,
		policyToken: midTermAdjustment.policyToken,
		midTermAdjustment: {
			...midTermAdjustment,
			vehicleDetails: {
				...midTermAdjustment.vehicleDetails,
				annualMileage,
			},
		},
	};

	return mtaHandleRating(product, command);
};

export const handleChangeOfVehicleRating = async ({
	midTermAdjustment,
	vehicleLookupResponse,
	values,
	product,
}: {
	midTermAdjustment: IMidTermAdjustmentState;
	vehicleLookupResponse: VehicleLookupResponse;
	values: IChangeOfVehiclePart2;
	product: Product;
}) => {
	let bodyTypeCheck = vehicleLookupResponse.vehicleInfo.bodyType?.code ?? '';

	if (product === Product.TAXI) {
		values.businessRadius = '0';
		bodyTypeCheck = values.bodyType ?? midTermAdjustment.vehicleDetails.bodyType ?? '';
	}
	const command = {
		changeType: [...midTermAdjustment.changeType, MtaChangeType.CHANGE_VEHICLE],
		mtaStartDate: midTermAdjustment.mtaStartDate,
		policyToken: midTermAdjustment.policyToken,

		midTermAdjustment: {
			...midTermAdjustment,
			vehicleDetails: {
				...midTermAdjustment.vehicleDetails,
				...vehicleLookupResponse.vehicleInfo,
				registration:
					vehicleLookupResponse.vehicleInfo.registrationNumber ?? midTermAdjustment.vehicleDetails.registration,
				operatingArea: values.operatingArea,
				bodyType: bodyTypeCheck,

				numberOfDoors: vehicleLookupResponse.vehicleInfo.numberOfDoors?.code ?? '',
				registeredKeeper: values.registeredKeeper,
				abiCode: vehicleLookupResponse.vehicleInfo.abiCode ?? '',
				engineSize: vehicleLookupResponse.vehicleInfo.cubicCapacity ?? 0,
				abiGroup: parseInt(vehicleLookupResponse.vehicleInfo.abiGroup ?? '0', 10),
				fuelType: vehicleLookupResponse.vehicleInfo.fuelType?.code ?? '',
				transmission: vehicleLookupResponse.vehicleInfo.transmission?.code ?? '',
				ownership: values.owner,
				isImported: values.imported === 'yes',
				value: parseInt(values.value, 10),
				make: vehicleLookupResponse.vehicleInfo.make?.code ?? '',
				model: vehicleLookupResponse.vehicleInfo.model?.code ?? '',
				coverDefinitionCode: values.levelOfCover,
				modifications: values.modifications,
				datePurchased: new Date(
					parseInt(values.purchaseDate.year, 10),
					parseInt(values.purchaseDate.month, 10) - 1,
					1,
				).toISOString(),
				countryOfRegistration: values.countryOfRegistration,
				businessRadius: values.businessRadius,
				hasInternalRacking: values.hasInternalRacking === 'yes',
				vehicleCarriesDangerousGoods: values.vehicleCarriesDangerousGoods === 'yes',
				vehicleVisitsHazardousSites: values.vehicleVisitsHazardousSites === 'yes',
				telematicsBox: values.telematicsBox === 'yes',
				yearOfManufacture: vehicleLookupResponse.vehicleInfo.year ?? 0,
				isLeftHandDrive: values.isLeftHandDrive === 'yes',
				vehicleUse: values.vehicleUse ?? midTermAdjustment.vehicleDetails.vehicleUse,
				grossWeight: parseInt(vehicleLookupResponse.vehicleInfo.grossWeight ?? '0'),
				vehicleOvernight: values.keptOvernight,
				overnightAddress:
					values && values.overnightAddressIsAway === 'yes'
						? values.overnightAddress
						: midTermAdjustment.policyDetails.policyholder.address,
			},
		},
	};

	return mtaHandleRating(product, command);
};

interface IMakePaymentIntent {
	paymentDetails: {
		saveCard: boolean;
		/** Amount to be taken */
		amount: number;
		billingAddress: Address;
	};
}

export const handleGetClientData = async () => {
	const client = new PaymentClient(undefined, API);
	return client.getClientData();
};

export const handleMakePaymentIntent = async (
	paymentProvider: PaymentProvider,
	midTermAdjustment: IMidTermAdjustmentState,
	makePaymentIntent: IMakePaymentIntent,
	preparePaymentSource: PreparePaymentSource = PreparePaymentSource.CARD,
) => {
	const client = new PaymentClient(undefined, API);
	const result = await client.makePaymentIntent({
		policyReference: midTermAdjustment.policyReference,
		transactionId: midTermAdjustment.transactionId,
		transactionType: TransactionType.MidTermAdjustment,
		paymentDetails: {
			...makePaymentIntent.paymentDetails,
			currencyCode: 'GBP',
		},
		paymentProvider,
		preparePaymentSource,
	});
	return result;
};

export const handTakeUpMidTermAdjustment = async (takeUpMtaCommand: TakeUpMidTermAdjustmentCommand) => {
	const client = new MidTermAdjustmentClient(undefined, API);
	const result = await client.takeUpMidTermAdjustment(takeUpMtaCommand);
	return result;
};

export const handleTakeUpMidTermAdjustment = async (
	takeUpMtaCommand: TakeUpBraintreeMidTermAdjustmentCommand,
	nonce?: string,
	deviceData?: string,
	idempotencyKey?: string,
) => {
	const client = new MidTermAdjustmentClientV2(undefined, API);
	const result = await client.takeUpMidTermAdjustment({
		...takeUpMtaCommand,
		nonce,
		deviceData,
		idempotencyKey,
	});
	return result;
};

export default useGetOrCreateMidTermAdjustment;
